import './Header.css';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppHeader, InfoIcon } from '@alchemiesolns/ui';
import { useAuthUser } from '@alchemiesolns/user-access';
import PiphWordmark from '../assets/piph-author-wordmark.svg';
import { signOutApp } from '../auth/signOutApp';
import { LearningResourcesPanel } from './LearningResourcesPanel';

interface HeaderProps {
  // Add header actions as needed in the future
}

export function Header({}: HeaderProps) {
  const navigate = useNavigate();
  const { user } = useAuthUser();
  const [menuOpen, setMenuOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (!menuRef.current?.contains(target)) {
        setMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleSignOut = async () => {
    await signOutApp();
    navigate('/login', { replace: true });
  };

  const displayName = user?.displayName?.trim() || user?.email?.split('@')[0] || 'there';

  return (
    <>
      <AppHeader theme="navy">
        <AppHeader.Brand>
          <h1 className="app-header-title app-header-brand">
            <Link to="/app" className="app-header-brand-link" aria-label="Piph Author — go to app">
              <img src={PiphWordmark} alt="" className="app-header-brand-piph" />
            </Link>
          </h1>
        </AppHeader.Brand>

        <AppHeader.Actions>
          <button
            type="button"
            className="app-header-info-button"
            onClick={() => {
              setMenuOpen(false);
              setResourcesOpen(true);
            }}
            aria-expanded={resourcesOpen}
            aria-haspopup="dialog"
            aria-label="Open Getting Started"
          >
            <InfoIcon width={20} height={20} />
          </button>
          <div className="app-header-user-menu" ref={menuRef}>
            <button
              type="button"
              className="app-header-user-menu-trigger"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              Hello {displayName}
              <span className="app-header-user-menu-caret" aria-hidden="true">
                ▾
              </span>
            </button>
            {menuOpen && (
              <div className="app-header-user-menu-dropdown" role="menu">
                <button
                  type="button"
                  className="app-header-user-menu-item"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/account');
                  }}
                >
                  Manage Account
                </button>
                <button
                  type="button"
                  className="app-header-user-menu-item"
                  role="menuitem"
                  onClick={() => void handleSignOut()}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </AppHeader.Actions>
      </AppHeader>
      <LearningResourcesPanel open={resourcesOpen} onClose={() => setResourcesOpen(false)} />
    </>
  );
}
